import { Input } from 'antd';
import Search from 'antd/es/transfer/search';
import HeaderBreadcrumb from '../../../components/Breadcrumb';
import { SourceRes } from '../../../core/dtos/sourceRes';
import {useEffect, useMemo, useState} from 'react';
import { createSource, deleteSource, getSources, updateSource } from '../../../services/sourceService';
import { HttpRes } from '../../../core/dtos/httpRes';
import Loader from '../../../components/Loader';
import formatDate from '../../../utils/formatDate';
import { SourceUpdateReq } from '../../../core/dtos/sourceUpdateReq';
import Pagination from 'antd/lib/pagination';
import { SourceRequestFilter } from '../../../core/types/SourceRequestFilter';
import {debounce} from 'lodash';
import createPageTitle from '../../../utils/createPageTitle';
import {Helmet} from 'react-helmet';
import getDateFromDatePicker from '../../../utils/getDateFromDatePicker';
import {
  Row,
  Form,
  Col,
  Card,
  Table,
  Button,
  Tooltip,
  DatePicker,
  Popconfirm,
  message,
} from 'antd';
import {
  EditOutlined,
  SaveOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import {RequestFilter} from '../../../core/types/RequestFilter';

const columns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
  },
  {
    title: 'Created At',
    dataIndex: 'createdAt',
    key: 'createdAt',
  },
  {
    title: '',
    dataIndex: 'actions',
    key: 'actions',
  },
];

const generateFilterParams = (filter: SourceRequestFilter): SourceRequestFilter => ({
  name: filter.name || undefined,
  limit: filter.limit || undefined,
  page: filter.page || undefined,
  fromCreatedAt: filter.fromCreatedAt || undefined,
  toCreatedAt: filter.toCreatedAt || undefined,
});

function Sources() {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [sources, setSources] = useState<HttpRes<SourceRes>>({
    items: [],
    pagination: {
      count: 0,
      current: 0,
      hasNext: false,
      hasPrev: false,
      itemCount: 0,
      next: null,
      prev: null
    },
    totalItemCount: 0
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [edit, setEdit] = useState<SourceUpdateReq>({
    name: '',
    _id: ''
  });
  const [filter, setFilter] = useState<SourceRequestFilter>({
    limit: 20,
    page: 1,
    fromCreatedAt: '',
    toCreatedAt: '',
  });

  const changeFilter = (changedFilter: Partial<RequestFilter>) => {
    if (!changedFilter.page) {
      setFilter({
        ...filter,
        page: 1
      });
    }
    setFilter((filter) => ({
      ...filter,
      ...changedFilter
    }));
  };

  const mapSources = () => sources.items.map(source => (
    {
      key: source._id,
      name: (
        <b>{source.name}</b>
      ),
      createdAt: (
        <span>{formatDate(source.createdAt)}</span>
      ),
      actions: (
        <>
          <Row className="ant-employed" gutter={[8, 0]} style={{justifyContent: 'flex-end'}}>
            <Col>
              <Tooltip title="Edit">
                <a href="#form">
                  <Button type="primary" shape="circle" icon={<EditOutlined />} onClick={() => {setEdit({_id: source._id, name: source.name}), form.setFieldValue('name', source.name);}} />
                </a>
              </Tooltip>
            </Col>
            <Col>
              <Popconfirm
                placement="bottomRight"
                title='Delete the Source'
                description='Are you sure to delete this source?'
                onConfirm={() => {
                  deleteSource(source._id)
                    .then(() => {
                      handleGetSources();
                    });
                }}
                okText="Delete"
                cancelText="Cancel"
              >
                <Tooltip title="Delete">
                  <Button type="primary" shape="circle" icon={<DeleteOutlined />} danger />
                </Tooltip>
              </Popconfirm>
            </Col>
          </Row>
        </>
      ),
    }
  ));

  const handleGetSources = () => {
    const filterParams: SourceRequestFilter = generateFilterParams(filter);

    getSources(filterParams)
      .then((body: HttpRes<SourceRes>) => {setSources(body);}
      )
      .finally(() => setIsLoading(false));
  };

  const handleSubmit = () => {
    if (edit._id !== '') {
      updateSource({...form.getFieldsValue(), _id: edit._id })
        .then(() => {
          handleGetSources();
          setEdit({
            name: '',
            _id: ''
          });
          form.setFieldValue('name', '');
          messageApi.open({
            type: 'success',
            content: 'Source successfully updated',
          });
        })
        .catch((err) => {
          messageApi.open({
            type: 'error',
            content: err.response?.data?.message || err.message
          });
        });
    } else {
      createSource(form.getFieldsValue())
        .then(() => {
          handleGetSources();
          form.setFieldValue('name', '');
          messageApi.open({
            type: 'success',
            content: 'Source successfully created',
          });
        })
        .catch((err) => {
          messageApi.open({
            type: 'error',
            content: err.response?.data?.message || err.message
          });
        });
    }
  };

  const handleSearchWithDebounce = useMemo(() => {
    return debounce(
      (e) => changeFilter({ name: e.target.value }),
      500
    );
  }, []);

  useEffect(() => {
    handleGetSources();
  }, [filter]);

  if (isLoading) {
    return (
      <Loader />
    );
  }
  return (
    <>
      <Helmet>
        <title>{createPageTitle('Sources')}</title>
      </Helmet>
      {contextHolder}
      <HeaderBreadcrumb items={['Sources']} />
      <div className="tabled">
        <Row gutter={[24, 16]}>
          <Col xs={24}>
            <Form name='Post' form={form} onFinish={handleSubmit} layout='vertical' id='form'>
              <Card
                bordered={false}
                className="criclebox tablespace mb-24"
                title="Create Source"
                extra={
                  <Button type="primary" htmlType='submit' shape="default" icon={<SaveOutlined />} size="large" style={{width: '120px'}}>
                    {edit._id !== '' ? 'Save' : 'Create'}
                  </Button>
                }
              >
                <div className="table-responsive">
                  <Row gutter={[24, 24]}>
                    <Col xs={24} style={{gap: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      <Form.Item
                        name='name'
                        rules={[{ required: true, message: 'Please enter source name' }, { min: 3, message: 'Source name must be longer than 2 characters' }]}
                        label='Source Name'
                        style={{width: '100%'}}
                      >
                        <Input placeholder="Source Name" size='large' />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </Card>
            </Form>
          </Col>
          <Col xs={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              title="List of Sources"
              extra={
                <Row className='table-filter'>
                  <Col xs={12} style={{marginRight: '10px'}}>
                    <Search placeholder="Search in source name" onChange={handleSearchWithDebounce} />
                  </Col>

                  <Col xs={9}>
                    <DatePicker.RangePicker
                      onChange={(e: any) => changeFilter(
                        getDateFromDatePicker(e)
                      )}
                    />
                  </Col>
                </Row>
              }
            >
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={mapSources()}
                  pagination={false}
                  className="ant-border-space"
                />
              </div>
            </Card>
          </Col>
        </Row>
      </div>
      <Card style={{marginTop: '16px', justifyContent: 'center', display:'flex'}}>
        <Pagination total={sources.totalItemCount} 
          current={filter.page} 
          onChange={(e) => changeFilter({ page: e })} 
          pageSize={filter.limit} 
          showTotal={(total) => `Total ${total} items`} 
          onShowSizeChange={(e, selectedNumber) => {changeFilter({ limit: selectedNumber});}} 
          defaultPageSize={20}
          showSizeChanger />
      </Card>
    </>
  );
}

export default Sources;
